import React, { useState } from 'react';
import { motion } from 'motion/react';
import { auth, db } from '../../firebase';
import { doc, updateDoc } from 'firebase/firestore';
import { EmailAuthProvider, reauthenticateWithCredential, updatePassword, updateProfile, verifyBeforeUpdateEmail } from 'firebase/auth';
import { useAuth } from '../../contexts/AuthContext';
import { User, Lock, Mail, AlertCircle, CheckCircle2, Moon, Sun } from 'lucide-react';

interface AccountPageProps {
  dark: boolean;
  toggleDark: () => void;
}

export function AccountPage({ dark, toggleDark }: AccountPageProps) {
  const { profile, user } = useAuth();
  
  // Section A - Display Name
  const [displayName, setDisplayName] = useState(profile?.displayName || '');
  const [displayNameLoading, setDisplayNameLoading] = useState(false);
  const [displayNameMsg, setDisplayNameMsg] = useState<{type: 'success' | 'error', text: string} | null>(null);

  // Section B - Email
  const [newEmail, setNewEmail] = useState('');
  const [emailCurrentPassword, setEmailCurrentPassword] = useState('');
  const [emailLoading, setEmailLoading] = useState(false);
  const [emailMsg, setEmailMsg] = useState<{type: 'success' | 'error', text: string} | null>(null);

  // Section C - Password
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [passwordLoading, setPasswordLoading] = useState(false);
  const [passwordMsg, setPasswordMsg] = useState<{type: 'success' | 'error', text: string} | null>(null);

  const getErrorMessage = (code: string) => {
    switch (code) {
      case 'auth/wrong-password':
      case 'auth/invalid-credential':
        return 'Current password is incorrect.';
      case 'auth/invalid-email':
        return 'Invalid email address.';
      case 'auth/email-already-in-use':
        return 'That email is already in use.';
      case 'auth/requires-recent-login':
        return 'Please sign out and back in, then try again.';
      case 'auth/weak-password':
        return 'Password is too weak (must be at least 6 characters).';
      default:
        return 'An unexpected error occurred. Please try again.';
    }
  };

  const handleUpdateName = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!user) return;
    setDisplayNameLoading(true);
    setDisplayNameMsg(null);
    try {
      await updateDoc(doc(db, 'users', user.uid), { displayName });
      await updateProfile(auth.currentUser!, { displayName });
      setDisplayNameMsg({ type: 'success', text: 'Display name updated successfully.' });
      setTimeout(() => setDisplayNameMsg(null), 3000);
    } catch (err: any) {
      setDisplayNameMsg({ type: 'error', text: err.message });
    } finally {
      setDisplayNameLoading(false);
    }
  };

  const handleUpdateEmail = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!user || user.email === newEmail) return;
    setEmailLoading(true);
    setEmailMsg(null);
    try {
      if (!user.email) throw new Error("No current email");
      const cred = EmailAuthProvider.credential(user.email, emailCurrentPassword);
      await reauthenticateWithCredential(auth.currentUser!, cred);
      await verifyBeforeUpdateEmail(auth.currentUser!, newEmail);
      setEmailMsg({ type: 'success', text: `A confirmation link was sent to ${newEmail}. Your email won't change until you click it, after which you may need to sign in again.` });
      setNewEmail('');
      setEmailCurrentPassword('');
    } catch (err: any) {
      setEmailMsg({ type: 'error', text: getErrorMessage(err.code) });
    } finally {
      setEmailLoading(false);
    }
  };

  const handleUpdatePassword = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!user) return;
    if (newPassword !== confirmPassword) {
      setPasswordMsg({ type: 'error', text: 'New passwords do not match.' });
      return;
    }
    if (newPassword.length < 6) {
      setPasswordMsg({ type: 'error', text: 'Password must be at least 6 characters.' });
      return;
    }
    setPasswordLoading(true);
    setPasswordMsg(null);
    try {
      if (!user.email) throw new Error("No current email");
      const cred = EmailAuthProvider.credential(user.email, currentPassword);
      await reauthenticateWithCredential(auth.currentUser!, cred);
      await updatePassword(auth.currentUser!, newPassword);
      setPasswordMsg({ type: 'success', text: 'Password updated successfully.' });
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
      setTimeout(() => setPasswordMsg(null), 3000);
    } catch (err: any) {
      setPasswordMsg({ type: 'error', text: getErrorMessage(err.code) });
    } finally {
      setPasswordLoading(false);
    }
  };

  return (
    <div className="max-w-2xl mx-auto space-y-8 animate-fade-in pb-20 mt-10">

      {/* Appearance Section */}
      <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} className="oi-card p-8">
        <div className="flex items-center gap-3 mb-6">
          {dark ? <Moon className="w-5 h-5 text-ink-500" /> : <Sun className="w-5 h-5 text-ink-500" />}
          <h2 className="text-xl font-serif font-medium text-ink">Appearance</h2>
        </div>
        <div className="flex items-center justify-between py-1">
          <div>
            <p className="text-sm font-medium text-ink">Interface theme</p>
            <p className="text-xs text-ink-500 mt-1">
              {dark ? 'Dark mode is active.' : 'Light mode is active.'}
            </p>
          </div>
          <button
            onClick={toggleDark}
            className="flex items-center gap-2 px-4 py-2.5 border border-ink-200 rounded-md text-[10px] font-medium uppercase tracking-widest text-ink-600 hover:text-ink hover:border-ink-300 transition-colors shrink-0 ml-6"
          >
            {dark ? <Sun className="w-3.5 h-3.5" /> : <Moon className="w-3.5 h-3.5" />}
            {dark ? 'Light Mode' : 'Dark Mode'}
          </button>
        </div>
      </motion.div>

      {/* Display Name Section */}
      <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} className="oi-card p-8">
        <div className="flex items-center gap-3 mb-6">
          <User className="w-5 h-5 text-ink-500" />
          <h2 className="text-xl font-serif font-medium text-ink">Profile</h2>
        </div>
        <form onSubmit={handleUpdateName} className="space-y-4">
          <div>
            <label className="block text-[10px] font-medium uppercase tracking-widest text-ink-500 mb-2">Display Name</label>
            <input
              type="text"
              required
              value={displayName}
              onChange={(e) => setDisplayName(e.target.value)}
              className="w-full px-4 py-3 bg-surface-2 border border-ink-200 rounded-md focus:ring-2 focus:ring-oxblood focus:border-transparent outline-none transition-all placeholder-ink-500 font-medium text-sm text-ink"
            />
          </div>
          {displayNameMsg && (
            <div className={`text-xs flex items-center gap-2 p-3 rounded-md ${displayNameMsg.type === 'success' ? 'bg-success-50 text-success-700' : 'bg-danger-50 text-danger-700'}`}>
              {displayNameMsg.type === 'success' ? <CheckCircle2 className="w-4 h-4" /> : <AlertCircle className="w-4 h-4" />}
              {displayNameMsg.text}
            </div>
          )}
          <div className="pt-2">
            <button
              type="submit"
              disabled={displayNameLoading}
              className="px-6 py-3 bg-oxblood hover:bg-oxblood-800 disabled:bg-ink-300 text-paper text-[10px] font-medium uppercase tracking-widest rounded-md transition-colors w-full sm:w-auto"
            >
              {displayNameLoading ? 'Saving...' : 'Save Name'}
            </button>
          </div>
        </form>
      </motion.div>

      {/* Email Section */}
      <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }} className="oi-card p-8">
        <div className="flex items-center gap-3 mb-6">
          <Mail className="w-5 h-5 text-ink-500" />
          <h2 className="text-xl font-serif font-medium text-ink">Email Address</h2>
        </div>
        <p className="text-sm text-ink-500 mb-6">Current email: <span className="font-semibold text-ink">{profile?.email}</span></p>
        <form onSubmit={handleUpdateEmail} className="space-y-4">
          <div>
            <label className="block text-[10px] font-medium uppercase tracking-widest text-ink-500 mb-2">New Email Address</label>
            <input
              type="email"
              required
              value={newEmail}
              onChange={(e) => setNewEmail(e.target.value)}
              className="w-full px-4 py-3 bg-surface-2 border border-ink-200 rounded-md focus:ring-2 focus:ring-oxblood focus:border-transparent outline-none transition-all placeholder-ink-500 font-medium text-sm text-ink"
            />
          </div>
          <div>
            <label className="block text-[10px] font-medium uppercase tracking-widest text-ink-500 mb-2">Current Password</label>
            <input
              type="password"
              required
              value={emailCurrentPassword}
              onChange={(e) => setEmailCurrentPassword(e.target.value)}
              className="w-full px-4 py-3 bg-surface-2 border border-ink-200 rounded-md focus:ring-2 focus:ring-oxblood focus:border-transparent outline-none transition-all placeholder-ink-500 font-medium text-sm text-ink"
            />
          </div>
          {emailMsg && (
            <div className={`text-xs flex items-center gap-2 p-3 rounded-md ${emailMsg.type === 'success' ? 'bg-success-50 text-success-700' : 'bg-danger-50 text-danger-700'}`}>
              {emailMsg.type === 'success' ? <CheckCircle2 className="w-4 h-4 shrink-0" /> : <AlertCircle className="w-4 h-4 shrink-0" />}
              <span>{emailMsg.text}</span>
            </div>
          )}
          <div className="pt-2">
            <button
              type="submit"
              disabled={emailLoading || !newEmail || !emailCurrentPassword}
              className="px-6 py-3 bg-oxblood hover:bg-oxblood-800 disabled:bg-ink-300 text-paper text-[10px] font-medium uppercase tracking-widest rounded-md transition-colors w-full sm:w-auto"
            >
              {emailLoading ? 'Sending...' : 'Change Email'}
            </button>
          </div>
        </form>
      </motion.div>

      {/* Password Section */}
      <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.2 }} className="oi-card p-8">
        <div className="flex items-center gap-3 mb-6">
          <Lock className="w-5 h-5 text-ink-500" />
          <h2 className="text-xl font-serif font-medium text-ink">Change Password</h2>
        </div>
        <form onSubmit={handleUpdatePassword} className="space-y-4">
          <div>
            <label className="block text-[10px] font-medium uppercase tracking-widest text-ink-500 mb-2">Current Password</label>
            <input
              type="password"
              required
              value={currentPassword}
              onChange={(e) => setCurrentPassword(e.target.value)}
              className="w-full px-4 py-3 bg-surface-2 border border-ink-200 rounded-md focus:ring-2 focus:ring-oxblood focus:border-transparent outline-none transition-all placeholder-ink-500 font-medium text-sm text-ink"
            />
          </div>
          <div>
            <label className="block text-[10px] font-medium uppercase tracking-widest text-ink-500 mb-2">New Password (min 6 characters)</label>
            <input
              type="password"
              required
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
              className="w-full px-4 py-3 bg-surface-2 border border-ink-200 rounded-md focus:ring-2 focus:ring-oxblood focus:border-transparent outline-none transition-all placeholder-ink-500 font-medium text-sm text-ink"
            />
          </div>
          <div>
            <label className="block text-[10px] font-medium uppercase tracking-widest text-ink-500 mb-2">Confirm New Password</label>
            <input
              type="password"
              required
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              className="w-full px-4 py-3 bg-surface-2 border border-ink-200 rounded-md focus:ring-2 focus:ring-oxblood focus:border-transparent outline-none transition-all placeholder-ink-500 font-medium text-sm text-ink"
            />
          </div>
          {passwordMsg && (
            <div className={`text-xs flex items-center gap-2 p-3 rounded-md ${passwordMsg.type === 'success' ? 'bg-success-50 text-success-700' : 'bg-danger-50 text-danger-700'}`}>
              {passwordMsg.type === 'success' ? <CheckCircle2 className="w-4 h-4 shrink-0" /> : <AlertCircle className="w-4 h-4 shrink-0" />}
              <span>{passwordMsg.text}</span>
            </div>
          )}
          <div className="pt-2">
            <button
              type="submit"
              disabled={passwordLoading || !currentPassword || !newPassword || !confirmPassword}
              className="px-6 py-3 bg-oxblood hover:bg-oxblood-800 disabled:bg-ink-300 text-paper text-[10px] font-medium uppercase tracking-widest rounded-md transition-colors w-full sm:w-auto"
            >
              {passwordLoading ? 'Updating...' : 'Update Password'}
            </button>
          </div>
        </form>
      </motion.div>
    </div>
  );
}
